import { Component, effect, EffectRef, ElementRef, inject, Signal, untracked, ViewChild } from '@angular/core';
import { ButtonComponent } from '../../../../shared/button/button.component';
import { MenuService } from '../../../../../services/components/menu.service';
import { SearchService } from '../../../../../services/components/search.service';
import { AppViews } from '../../../../../models/enums/dvm/DvmViews.enum';
import { DvmService } from '../../../../../services/dvm.service';
import { Menu, MenuKeys } from '@models/interfaces/components/Menu.interface';
import anime from 'animejs/lib/anime.es.js';
import { LayoutService } from '@services/layout.service';

@Component({
  selector: 'app-menu-buttons',
  standalone: true,
  imports: [
    ButtonComponent
  ],
  templateUrl: './menu-buttons.component.html',
  styleUrl: './menu-buttons.component.css'
})
export class MenuButtonsComponent {

  // VIEW CHILDREN
  @ViewChild('menuButtons', {static: false})
  public readonly  menuButtons!:  ElementRef<HTMLDivElement>;

  // SERVICES
  private readonly MenuService:   MenuService               = inject(MenuService);
  private readonly searchService: SearchService             = inject(SearchService);
  private readonly layoutService:  LayoutService            = inject(LayoutService);

  // GLOBAL STATE
  public readonly  view:          Signal<AppViews|null>     = inject(DvmService).view;
  public readonly  isMobile:      Signal<boolean>           = this.layoutService.isMobile;
  public readonly  menuData:      Map<MenuKeys, Menu>       = this.MenuService.menus;
  public readonly  selectedMenu:  Menu | undefined          = this.MenuService.menuSelected;

  // SEARCH STATE
  private readonly searchActive:  Signal<boolean|null>      = this.searchService.searchActive;
  private readonly duration:      number                    = this.searchService.duration;


  // ANIMATION STATE
  protected showMenus:            boolean                   = false;
  protected disabled:             boolean                   = false;
  protected timer:                ReturnType<typeof setTimeout> | null  = null;

  // // EFFECTS
  public onToggleSearch:          EffectRef                 = effect((): void => {

      const isSearchActive: boolean | null = this.searchActive();
      const isSeatMap:      boolean        = untracked(this.view) === AppViews.Seatmap;
      
      if (isSearchActive === null || isSeatMap) return;

      if(isSearchActive){

        this.toggleMenuVisibility(false);

        if(this.MenuService.showContainer()) this.MenuService.closeMenuContainer() // Si está abierto, emulamos que pulsamos el mismo menú para cerrarlo.

        setTimeout((): boolean => this.showMenus = false, this.duration);

      }
      else {
        
        if(this.timer) clearTimeout(this.timer);

        this.showMenus = true;

        this.timer = setTimeout((): void => this.toggleMenuVisibility(true), this.duration);

      }


  }, {allowSignalWrites: true});

  public onViewChange:            EffectRef                 = effect((): void => {

        const view: AppViews | null = this.view();

        if(view === null) return;

        if(this.timer) clearTimeout(this.timer);

        switch(view) {

          case AppViews.Topview:
          case AppViews.Main:
            
            this.toggleMenuVisibility(true);
            break;

          case AppViews.Seatmap:
          case AppViews.Pano:
            this.toggleMenuVisibility(false);
            break;

        }


  },{allowSignalWrites: true});


  // GETTERS
  public get menus(): Menu[] {
          
    const menus: Menu[] = Array.from(this.menuData.values());

    return menus.sort((a, b) => a.order - b.order);
  
  }


  // METHODS
  public toggleMenu(key:MenuKeys): void {

    this.MenuService.toggleMenuContainer(key);
    
  }

  public isMenuActive(key: MenuKeys): boolean {
    return (
      this.MenuService.menuSelected?.id === key || 
      this.MenuService.hasMenuSelectedItem(key)
    );
  }

  private toggleMenuVisibility(show: boolean): void {
    
    if(!this.showMenus && show) this.showMenus = true; 

    this.disabled = !show;
    
    const targets: HTMLCollection = this.menuButtons.nativeElement.children;

    anime({
      targets:    targets,
      duration:   show ? this.duration + 150 : this.duration,
      easing:     'easeInOutSine',
      opacity:    show ? 1 : 0,
      translateY: show ? 0 : this.isMobile() ? 20 : -20,
      delay:      anime.stagger(100),
      complete:   (): boolean => this.showMenus = show
    });



  }

}
